/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { ElementType, MouseEvent, MouseEventHandler } from 'react'
import styled, { CSSObject, DefaultTheme } from 'styled-components'

import { accessibilityAndTestId } from 'libs/accessibilityAndTestId'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { ColorsType } from 'theme/types'
import { customFocusOutline } from 'ui/theme/customFocusOutline/customFocusOutline'

import { ButtonBase } from './ButtonBase'
import { ButtonColorValue, ButtonVariant, ButtonWebProps } from './types'

export const Button = (props: ButtonWebProps) => {
  const { testID, onPress, onLongPress, href, target, type = 'button' } = props

  const defaultAccessibilityRole = href ? AccessibilityRole.LINK : AccessibilityRole.BUTTON
  const ButtonComponent: ElementType = href ? Link : ButtonElement
  const buttonLinkProps = { href, target }

  const clickHandler =
    (type: ButtonWebProps['type'], handler?: MouseEventHandler<HTMLButtonElement>) =>
    (event: MouseEvent<HTMLButtonElement>) => {
      if (type === 'submit' && handler) event.preventDefault()
      handler?.(event)
    }

  return (
    <ButtonBase
      {...props}
      defaultAccessibilityRole={defaultAccessibilityRole}
      renderContainer={({ content, isDisabled, isLoading, ...containerProps }) => {
        const pressHandler = isDisabled || isLoading ? undefined : onPress
        const longPressHandler = isDisabled || isLoading ? undefined : onLongPress

        const onClick = clickHandler(type, pressHandler)
        const onDoubleClick = clickHandler(type, longPressHandler)

        return (
          <ButtonComponent
            {...accessibilityAndTestId(containerProps.accessibilityLabel, testID)}
            aria-label={containerProps.accessibilityLabel}
            accessibilityRole={containerProps.accessibilityRole}
            accessibilityHint={containerProps.accessibilityHint}
            fullWidth={containerProps.fullWidth}
            disabled={isDisabled}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onMouseEnter={containerProps.onMouseEnter}
            onMouseLeave={containerProps.onMouseLeave}
            backgroundColor={containerProps.backgroundColor}
            borderColor={containerProps.borderColor}
            borderWidth={containerProps.borderWidth}
            paddingVertical={containerProps.paddingVertical}
            paddingHorizontal={containerProps.paddingHorizontal}
            type={href ? undefined : type}
            tabIndex={isDisabled || isLoading ? undefined : 0}
            {...buttonLinkProps}>
            {content}
          </ButtonComponent>
        )
      }}
    />
  )
}

type ContainerStyleProps = {
  variant: ButtonVariant
  backgroundColor?: ButtonColorValue
  borderColor?: ColorsType
  borderWidth?: number
  fullWidth?: boolean
  paddingVertical: number
  paddingHorizontal: number
}

const buttonContainerStyles = ({
  theme,
  borderColor,
  borderWidth,
  backgroundColor,
  fullWidth,
  paddingVertical,
  paddingHorizontal,
  variant,
}: ContainerStyleProps & { theme: DefaultTheme }): CSSObject => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: variant === 'tertiary' ? undefined : theme.designSystem.size.borderRadius.m,
  paddingTop: paddingVertical,
  paddingBottom: paddingVertical,
  paddingLeft: paddingHorizontal,
  paddingRight: paddingHorizontal,
  width: fullWidth ? '100%' : 'auto',
  maxWidth: theme.contentPage.maxWidth,
  alignSelf: fullWidth ? 'center' : undefined,
  borderStyle: borderColor ? 'solid' : undefined,
  borderColor: borderColor,
  borderWidth: borderColor ? borderWidth : 0,
  backgroundColor: backgroundColor,
  cursor: 'pointer',
  outline: 'none',
  display: 'inline-flex',
  overflow: 'hidden',
  textDecoration: 'none',
  boxSizing: 'border-box',
  ['&:disabled']: {
    cursor: 'initial',
  },
  ...customFocusOutline({ theme }),
})

const ButtonElement = styled.button<ContainerStyleProps>(buttonContainerStyles)
const Link = styled.a<ContainerStyleProps>(buttonContainerStyles)
